// ============================================================================
// Indicador de conexión (barra superior) y aviso "Sin conexión"
// (modo contingencia, paso 2 · oct-2026).
//
// El indicador está SIEMPRE visible y mide "guardado", no "hay wifi":
//   gris  Comprobando…               sin respuesta reciente (nunca arranca en verde)
//   verde En línea · todo guardado    respondió hace < 15 s y nada en espera
//   ámbar Guardando N cambios…        un cambio enviado sin confirmar
//   ámbar Sin actualizaciones en vivo responde, pero el tiempo real se cayó
//   rojo  Sin conexión · N sin subir  3 pings fallidos seguidos
// En verde es chico (solo el punto en pantallas angostas); crece cuando algo
// anda mal. Tocarlo muestra la hora del último cambio guardado.
//
// Hasta que exista la cola offline (paso 3), sin conexión NADA se guarda, y
// el aviso rojo lo dice así.
// ============================================================================
import { useEffect, useRef, useState } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';
import { useConexion } from '../hooks/use-conexion';
import { useUser } from '../context/user-context';
import { abrirCaja } from '../contingencia/caja';
import { useTextos } from '../context/regional-context';
import { textosConexion } from '../../i18n/modulos/conexion';
import type { EstadoConexion } from '../../lib/conexion';
import type { VarsTraduccion } from '../../i18n/traducir';

const CHIP: Record<EstadoConexion, string> = {
  comprobando: 'bg-[var(--surface-2)] text-[var(--muted-ink)]',
  en_linea: 'bg-[var(--ok-bg)] text-[var(--ok-ink)]',
  guardando: 'bg-[var(--warn-bg)] text-[var(--warn-ink)]',
  pendiente_local: 'bg-[var(--warn-bg)] text-[var(--warn-ink)]',
  sin_vivo: 'bg-[var(--warn-bg)] text-[var(--warn-ink)]',
  sin_conexion: 'bg-[var(--danger-bg)] text-[var(--danger-ink)]',
};

const PUNTO: Record<EstadoConexion, string> = {
  comprobando: 'bg-[var(--muted-2)]',
  en_linea: 'bg-[var(--ok)]',
  guardando: 'bg-[var(--warn)] animate-pulse',
  pendiente_local: 'bg-[var(--warn)]',
  sin_vivo: 'bg-[var(--warn)]',
  sin_conexion: 'bg-[var(--danger)] animate-pulse',
};

function Punto({ estado }: { estado: EstadoConexion }) {
  return <span aria-hidden="true" className={`inline-block size-2.5 shrink-0 rounded-full ${PUNTO[estado]}`} />;
}

type ClaveConexion = keyof typeof textosConexion.es;
type Traductor = (clave: ClaveConexion, vars?: VarsTraduccion) => string;

function etiquetaDe(t: Traductor, estado: EstadoConexion, pendientes: number, locales: number): string {
  const sinSubir = pendientes + locales;
  const etiqueta: Record<EstadoConexion, string> = {
    comprobando: t('comprobando'),
    en_linea: t('enLinea'),
    guardando: pendientes === 1 ? t('guardando1') : t('guardandoN', { n: pendientes }),
    pendiente_local: locales === 1 ? t('pendienteLocal1') : t('pendienteLocalN', { n: locales }),
    sin_vivo: t('sinVivo'),
    sin_conexion: sinSubir ? t('sinConexionN', { n: sinSubir }) : t('sinConexion'),
  };
  return etiqueta[estado];
}

export function IndicadorConexion() {
  const t = useTextos(textosConexion);
  const { estado, foto, pendientes, copiaTomadaEn, comprobarAhora } = useConexion();
  const { canViewModule } = useUser();
  const etiqueta = etiquetaDe(t, estado, pendientes, foto.pedidosLocales);
  const puedeCaja = canViewModule('orders') && (estado === 'sin_conexion' || foto.pedidosLocales > 0);
  const [abierto, setAbierto] = useState(false);
  const [comprobando, setComprobando] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const explica: Record<EstadoConexion, string> = {
    comprobando: t('explicaComprobando'),
    en_linea: t('explicaEnLinea'),
    guardando: t('explicaGuardando'),
    pendiente_local: t('explicaPendienteLocal'),
    sin_vivo: t('explicaSinVivo'),
    sin_conexion: t('explicaSinConexion'),
  };

  const hace = (ts: number | null) => {
    if (ts == null) return t('todaviaNo');
    const s = Math.max(0, Math.round((foto.ahora - ts) / 1000));
    return s < 120 ? t('haceSeg', { s }) : t('haceMin', { m: Math.round(s / 60) });
  };
  const hora = (ts: number) =>
    new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const comprobar = async () => {
    setComprobando(true);
    try { await comprobarAhora(); } finally { setComprobando(false); }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setAbierto(a => !a)}
        data-testid="indicador-conexion"
        data-estado={estado}
        aria-label={`${t('etiqueta')}: ${etiqueta}`}
        aria-expanded={abierto}
        title={etiqueta}
        className={`press inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl border-0 px-3 text-[12.5px] font-bold whitespace-nowrap ${CHIP[estado]}`}
      >
        <Punto estado={estado} />
        {/* Verde: chico (solo el punto hasta pantallas medianas). El resto crece. */}
        <span className={estado === 'en_linea' ? 'hidden md:inline' : 'hidden sm:inline'}>{etiqueta}</span>
      </button>
      {/* Anuncio para lectores de pantalla solo cuando cambia el estado. */}
      <span className="sr-only" aria-live="polite">{etiqueta}</span>

      {abierto && (
        <div
          role="dialog"
          aria-label={t('etiqueta')}
          className="fixed inset-x-4 top-[76px] z-50 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4 text-[var(--ink)] shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-[calc(100%+8px)] sm:w-80"
        >
          <div className="flex items-center gap-2 text-[14px] font-extrabold">
            <Punto estado={estado} />
            {etiqueta}
          </div>
          <p className="mt-1.5 text-[13px] leading-snug text-[var(--muted-ink)]">{explica[estado]}</p>
          <dl className="mt-3 space-y-1.5 text-[13px]">
            <div className="flex justify-between gap-3">
              <dt className="text-[var(--muted-ink)]">{t('ultimoGuardado')}</dt>
              <dd className="text-right font-bold tabular-nums" data-testid="ultimo-guardado">
                {foto.ultimoGuardadoOk ? hora(foto.ultimoGuardadoOk) : t('ningunoAun')}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-[var(--muted-ink)]">{t('servidorRespondio')}</dt>
              <dd className="font-bold tabular-nums">{hace(foto.ultimoOk)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-[var(--muted-ink)]">{t('copiaContingencia')}</dt>
              <dd className="text-right font-bold tabular-nums" data-testid="copia-contingencia">{hace(copiaTomadaEn)}</dd>
            </div>
            {pendientes > 0 && (
              <div className="flex justify-between gap-3">
                <dt className="text-[var(--muted-ink)]">{t('esperandoConfirmacion')}</dt>
                <dd className="font-bold tabular-nums">{pendientes}</dd>
              </div>
            )}
          </dl>
          <button
            type="button"
            onClick={comprobar}
            disabled={comprobando}
            className="press mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2 text-[13px] font-bold text-[var(--ink)] disabled:opacity-60"
          >
            <RefreshCw className={`size-4 ${comprobando ? 'animate-spin' : ''}`} />
            {comprobando ? t('comprobando') : t('comprobarAhora')}
          </button>
          {puedeCaja && (
            <button
              type="button"
              onClick={() => { setAbierto(false); abrirCaja(); }}
              className="press mt-2 inline-flex w-full cursor-pointer items-center justify-center rounded-xl border-0 bg-[var(--warn)] px-3 py-2 text-[13px] font-extrabold text-[var(--ink)]"
            >
              {t('abrirCaja')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Franja roja bajo la barra superior mientras no haya conexión. No se puede cerrar. */
export function AvisoSinConexion() {
  const t = useTextos(textosConexion);
  const { estado, comprobarAhora } = useConexion();
  const { canViewModule } = useUser();
  if (estado !== 'sin_conexion') return null;
  return (
    <div
      role="alert"
      data-testid="aviso-sin-conexion"
      className="flex shrink-0 items-center gap-3 bg-[var(--danger)] px-4 py-2.5 text-[13.5px] text-white"
    >
      <WifiOff aria-hidden="true" className="size-4 shrink-0" />
      <p className="m-0 flex-1 leading-snug">
        <b>{t('avisoTitulo')}</b> {canViewModule('orders') ? t('avisoTextoCaja') : t('avisoTexto')}
      </p>
      {canViewModule('orders') && (
        <button
          type="button"
          onClick={abrirCaja}
          data-testid="abrir-caja-contingencia"
          className="press shrink-0 cursor-pointer rounded-lg border-0 bg-white px-3 py-1 text-[12.5px] font-extrabold text-[var(--danger-ink)]"
        >
          {t('abrirCaja')}
        </button>
      )}
      <button
        type="button"
        onClick={() => { void comprobarAhora(); }}
        className="press shrink-0 cursor-pointer rounded-lg border border-white/50 bg-white/15 px-3 py-1 text-[12.5px] font-bold text-white"
      >
        {t('avisoReintentar')}
      </button>
    </div>
  );
}
